import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

interface Pin {
  id: number;
  name: string;
  x: string;
  y: string;
}

export default function BiteMarkLanding() {
  const [hoveredPin, setHoveredPin] = useState<number | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Refs for smooth scrolling
  const howRef = useRef<HTMLDivElement | null>(null);
  const featuresRef = useRef<HTMLDivElement | null>(null);

  /* -------------------- DATA -------------------- */

  const heroGradient =
    "bg-gradient-to-b from-[#FF9A5A] via-[#FF6B6B] to-[#FF5F52]";

  const pins: Pin[] = [
    { id: 1, name: "Joe’s Smash Burger", x: "20%", y: "40%" },
    { id: 2, name: "Little Napoli Pizza", x: "65%", y: "28%" },
    { id: 3, name: "Golden Ramen", x: "40%", y: "65%" },
    { id: 4, name: "Café Luna", x: "75%", y: "55%" },
  ];

  const steps = [
    {
      icon: "📲",
      title: "Save from Social",
      desc: "Drop a TikTok, Instagram, or YouTube food video.",
    },
    {
      icon: "🧠",
      title: "Auto-Detect the Spot",
      desc: "BiteMark finds the restaurant & location for you.",
    },
    {
      icon: "⭐",
      title: "Rate After You Eat",
      desc: "No fake hype. Rate only places you’ve tried.",
    },
    {
      icon: "🗺️",
      title: "Build Your Food Map",
      desc: "Your personal map grows bite by bite.",
    },
  ];

  const features = [
    {
      icon: "🎥",
      title: "Social → Map",
      desc: "Turn food videos into real saved locations.",
    },
    {
      icon: "🧠",
      title: "Smart Detection",
      desc: "AI finds restaurants even when not tagged.",
    },
    {
      icon: "⭐",
      title: "Honest Ratings",
      desc: "Rate places after you’ve actually eaten there.",
    },
    {
      icon: "❤️",
      title: "Favorites",
      desc: "Save the spots worth coming back to.",
    },
    {
      icon: "🗺️",
      title: "Food Memory",
      desc: "A visual record of everywhere you’ve eaten.",
    },
    {
      icon: "🔒",
      title: "Private by Default",
      desc: "Your food data stays yours.",
    },
  ];

  /* -------------------- EFFECTS -------------------- */



  const scrollToRef = (ref: React.RefObject<HTMLDivElement | null>) => {
    if (ref.current) {
      window.scrollTo({
        top: ref.current.offsetTop - 80,
        behavior: "smooth",
      });
    }
  };


  /* -------------------- UI -------------------- */

  return (
    <div className="min-h-screen bg-[#FFF8F4] text-[#2B1A14] antialiased">
      {/* ================= HEADER ================= */}
      <header className="fixed top-0 w-full bg-[#FFF8F4]/95 backdrop-blur-md z-50 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className={`w-12 h-12 rounded-xl ${heroGradient} flex items-center justify-center shadow-lg`}
            >
              <img
                src="/BiteMark.png"
                alt="BiteMark logo"
                className="w-9 h-9 object-contain"
              />
            </div>
            <div>
              <div className="font-bold text-lg">BiteMark</div>
              <div className="text-xs text-[#7A3E2E] -mt-0.5">
                Find it • Bite it • Remember it
              </div>
            </div>
          </div>

          <div className="hidden md:flex gap-6 items-center">
            <button
              className="text-[#2B1A14] font-semibold hover:text-[#FF4D4D] transition"
              onClick={() => scrollToRef(howRef)}
            >
              How It Works
            </button>
            <button
              className="text-[#2B1A14] font-semibold hover:text-[#FF4D4D] transition"
              onClick={() => scrollToRef(featuresRef)}
            >
              Features
            </button>
            <Link
              to="/waitlist"
              className="px-4 py-2 rounded-xl bg-[#FF4D4D] text-white font-semibold shadow hover:shadow-lg transition"
            >
              Join Waitlist
            </Link>
          </div>

          <button
            className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-xl border border-[#F0B9A8] text-[#7A3E2E] hover:text-[#FF4D4D] transition"
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? "✕" : "☰"}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t border-[#F7D7C7] px-6 pb-4">
            <div className="flex flex-col gap-3 pt-4">
              <button
                className="text-left font-semibold text-[#2B1A14] hover:text-[#FF4D4D] transition"
                onClick={() => {
                  setMobileMenuOpen(false);
                  scrollToRef(howRef);
                }}
              >
                How It Works
              </button>
              <button
                className="text-left font-semibold text-[#2B1A14] hover:text-[#FF4D4D] transition"
                onClick={() => {
                  setMobileMenuOpen(false);
                  scrollToRef(featuresRef);
                }}
              >
                Features
              </button>
              <Link
                to="/waitlist"
                className="inline-flex items-center justify-center rounded-xl bg-[#FF4D4D] py-2 text-white font-semibold shadow transition"
                onClick={() => setMobileMenuOpen(false)}
              >
                Join Waitlist
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* ================= HERO ================= */}
      <main className="max-w-6xl mx-auto px-6 pt-28">
        <section className="grid lg:grid-cols-2 gap-12 items-center mt-12">
          {/* TEXT */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ type: "spring", stiffness: 220, damping: 22 }}
          >
            <h1 className="text-4xl md:text-5xl font-extrabold leading-tight">
              Your food memory,
              <br />
              <span className="text-[#FF4D4D]">placed on the map.</span>
            </h1>

            <p className="mt-4 text-[#5C2F24] max-w-xl">
              BiteMark turns food videos into saved places — visually organized
              so you always know where to eat next.
            </p>

            <div className="mt-8">
              <Link
                to="/waitlist"
                className="px-6 py-3 rounded-2xl bg-[#FF4D4D] text-white font-semibold shadow-lg hover:shadow-xl transition"
              >
                Join the Waitlist
              </Link>
            </div>
          </motion.div>

          {/* ================= PHONE MAP ================= */}
          <motion.div className="flex justify-center">
            <div className="w-[340px] h-[680px] rounded-[2.5rem] bg-white shadow-2xl border border-[#FFD1C2] overflow-hidden relative">
              <div className={`${heroGradient} h-14`} />

              {/* My Map Header */}
              <div className="absolute top-16 w-full text-center z-10">
                <div className="font-bold text-lg text-[#2B1A14] bg-white/80 rounded-full py-1 px-3 inline-block shadow">
                  My Map
                </div>
              </div>

              <div className="absolute inset-x-0 top-14 bottom-0">
                <img
                  src="/map.png"
                  alt="City map"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-[#FF4D4D]/5" />

                {pins.map((pin) => {
                  const active = hoveredPin === pin.id;

                  return (
                    <motion.div
                      key={pin.id}
                      className="absolute cursor-pointer"
                      style={{ left: pin.x, top: pin.y }}
                      animate={{
                        scale: active ? 1.25 : 1,
                        y: active ? -6 : 0,
                      }}
                      transition={{ type: "spring", stiffness: 400 }}
                      onMouseEnter={() => setHoveredPin(pin.id)}
                      onMouseLeave={() => setHoveredPin(null)}
                    >
                      {/* LABEL */}
                      <AnimatePresence>
                        {active && (
                          <motion.div
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            transition={{ type: "spring", stiffness: 300 }}
                            className="absolute -top-16 left-1/2 -translate-x-1/2 min-w-max bg-white px-4 py-2 rounded-lg shadow text-xs font-semibold whitespace-nowrap"
                          >
                            {pin.name}
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {/* PIN ICON (classic pin shape) */}
                      <div className="relative w-6 h-10 flex justify-center items-start">
                        <motion.div
                          className="absolute w-6 h-10"
                          initial={{ y: 0 }}
                          animate={{ y: [0, -4, 0] }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut",
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            className="w-6 h-10"
                          >
                            <path
                              fill="#FF4D4D"
                              d="M12 2C8.1 2 5 5.1 5 9c0 5.3 7 13 7 13s7-7.7 7-13c0-3.9-3.1-7-7-7zm0 9.5c-1.4 0-2.5-1.1-2.5-2.5S10.6 6.5 12 6.5s2.5 1.1 2.5 2.5S13.4 11.5 12 11.5z"
                            />
                          </svg>
                        </motion.div>

                        <div className="absolute inset-0 rounded-full bg-[#FF4D4D]/30 animate-ping" />
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </section>


        {/* ================= HOW IT WORKS ================= */}
        <section className="mt-32 text-center" ref={howRef}>
          <h2 className="text-3xl font-bold mb-12">How It Works</h2>

          <div className="max-w-5xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
                className="bg-white rounded-3xl p-6 shadow-md border border-[#FFD1C2] hover:scale-105 transition-transform"
              >
                <div className="text-4xl mb-3">{s.icon}</div>
                <div className="font-semibold text-lg mb-1">{s.title}</div>
                <p className="text-sm text-[#6B3A2B]">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ================= FEATURES ================= */}
        <section className="mt-32 pb-32" ref={featuresRef}>
          <h2 className="text-3xl font-bold text-center mb-12">
            Built for Food People
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((f, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -6, scale: 1.03 }}
                className="bg-white rounded-2xl p-6 text-center border border-[#FFD1C2] shadow-md"
              >
                <div className="text-5xl mb-4">{f.icon}</div>
                <div className="font-semibold text-lg mb-2">{f.title}</div>
                <div className="text-sm text-[#6B3A2B]">{f.desc}</div>
              </motion.div>
            ))}
          </div>
        </section>
      </main>

      {/* ================= FOOTER ================= */}
      <footer className="border-t border-[#FFD1C2] py-10 text-center text-sm text-[#7A3E2E] flex flex-col sm:flex-row justify-center items-center gap-4">
        <div>© {new Date().getFullYear()} BiteMark — All rights reserved.</div>
        <div className="flex gap-4">
          <button
            className="text-[#2B1A14] hover:text-[#FF4D4D] transition font-semibold"
            onClick={() => (window.location.href = "/privacy-policy")}
          >
            Privacy Policy
          </button>
          <button
            className="text-[#2B1A14] hover:text-[#FF4D4D] transition font-semibold"
            onClick={() => (window.location.href = "/terms-of-use")}
          >
            Terms of Use
          </button>
        </div>
      </footer>
    </div>
  );
}
